import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Bed, Bath, ArrowRight } from 'lucide-react';
import { Property } from '../../types';
import { Badge } from '../common/Badge';
import { generatePropertySlug, extractCityFromLocation, extractSuburbFromLocation } from '../../utils/seo/slugs';

export const PropertyCard: React.FC<{ property: Property; distance?: number }> = ({ property, distance }) => {
  const city = extractCityFromLocation(property.location);
  const suburb = extractSuburbFromLocation(property.location);
  const locationName = suburb === city ? city : `${suburb}, ${city}`;

  // Calculate Availability Logic
  let availabilityBadge;

  if (property.status === 'RENTED') {
    availabilityBadge = <Badge color="gray">Rented</Badge>;
  } else if (property.rooms && property.rooms.length > 0) {
    // Check specific rooms if defined
    // A room is available if r.available is explicitly true or undefined (legacy support)
    // A room is taken if r.available is explicitly false
    const availableRooms = property.rooms.filter(r => r.available !== false);
    const totalRooms = property.rooms.length;

    if (availableRooms.length === 0) {
      availabilityBadge = <Badge color="gray">Rented</Badge>;
    } else if (availableRooms.length === totalRooms) {
      availabilityBadge = <Badge color="green">Available</Badge>;
    } else {
      // Partial availability
      availabilityBadge = <Badge color="yellow">{availableRooms.length} Rooms Left</Badge>;
    }
  } else {
    // Fallback for legacy whole-unit properties
    availabilityBadge = <Badge color={property.status === 'AVAILABLE' ? 'green' : 'gray'}>{property.status}</Badge>;
  }

  // Generate SEO URL
  const propertyUrl = `/room/${property.slug || generatePropertySlug(property.title, property.location, property.id)}`;

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col h-full overflow-hidden relative">
      <div className="relative h-56 overflow-hidden">
        <Link to={propertyUrl} className="block w-full h-full">
          <img src={property.image} alt={property.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
        </Link>
        <div className="absolute top-3 right-3 flex gap-2">
          {availabilityBadge}
        </div>

        {/* Distance Pin Indicator */}
        {distance !== undefined && (
          <div className="absolute top-3 left-3 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded-full flex items-center shadow-md">
            <MapPin className="w-3 h-3 mr-1 fill-current" />
            {distance.toFixed(1)} km
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-4 pointer-events-none">
          <p className="text-white font-bold text-lg">R{property.price}<span className="text-sm font-normal opacity-90">/mo</span></p>
        </div>
      </div>
      <div className="p-5 flex flex-col flex-1">
        <div className="mb-3">
          <p className="text-xs text-[#2f5c96] font-bold uppercase tracking-wide mb-1">{property.type}</p>
          <Link to={propertyUrl} className="block">
            <h3 className="text-lg font-bold text-gray-900 line-clamp-1 group-hover:text-[#2f5c96] transition-colors">{property.title}</h3>
          </Link>
        </div>
        <div className="flex items-center text-gray-500 text-sm mb-4">
          <MapPin className="w-4 h-4 mr-1 flex-shrink-0 text-[#c19cca]" />
          <span className="truncate font-medium">{property.locationName || locationName}</span>
        </div>

        {/* Amenities Preview (First 3) */}
        <div className="flex flex-wrap gap-1 mb-4">
          {property.amenities.slice(0, 3).map(am => (
            <span key={am} className="text-[10px] bg-gray-100 text-gray-600 px-2 py-0.5 rounded">{am}</span>
          ))}
          {property.amenities.length > 3 && <span className="text-[10px] text-gray-400">+{property.amenities.length - 3}</span>}
        </div>

        <div className="flex items-center justify-between pt-4 border-t border-gray-100 text-sm text-gray-600 mt-auto">
          <div className="flex gap-4">
            <span className="flex items-center"><Bed className="w-4 h-4 mr-1 text-gray-400" /> {property.bedrooms}</span>
            <span className="flex items-center"><Bath className="w-4 h-4 mr-1 text-gray-400" /> {property.bathrooms}</span>
          </div>
          <Link to={propertyUrl} className="text-[#2f5c96] font-bold hover:text-[#c19cca] flex items-center transition-colors">
            View <ArrowRight className="w-4 h-4 ml-1" />
          </Link>
        </div>
      </div>
    </div>
  );
};